import { ComponentType } from 'react';

import {
  AlignKind,
  DistributeAxis,
} from '@/components/diagrams/FlowChart/utils/alignment';
import { Divider } from '@/components/Divider';
import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { AlignHorizontalCenterIcon } from '@/components/icons/AlignHorizontalCenterIcon';
import { AlignHorizontalLeftIcon } from '@/components/icons/AlignHorizontalLeftIcon';
import { AlignHorizontalRightIcon } from '@/components/icons/AlignHorizontalRightIcon';
import { AlignVerticalBottomIcon } from '@/components/icons/AlignVerticalBottomIcon';
import { AlignVerticalCenterIcon } from '@/components/icons/AlignVerticalCenterIcon';
import { AlignVerticalTopIcon } from '@/components/icons/AlignVerticalTopIcon';
import { IconProps } from '@/components/icons/CircleIcon';
import { DistributeHorizontalSpacingIcon } from '@/components/icons/DistributeHorizontalSpacingIcon';
import { DistributeVerticalSpacingIcon } from '@/components/icons/DistributeVerticalSpacingIcon';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { COLORS } from '@/styles/tokens/colors';

interface AlignOption {
  label: string;
  kind: AlignKind;
  Icon: ComponentType<IconProps>;
}

// X-axis kinds align across the whole selection; rendered as the first group.
const X_ALIGN_OPTIONS: AlignOption[] = [
  { label: 'Left', kind: 'left', Icon: AlignHorizontalLeftIcon },
  { label: 'Center', kind: 'centerX', Icon: AlignHorizontalCenterIcon },
  { label: 'Right', kind: 'right', Icon: AlignHorizontalRightIcon },
];

// Y-axis kinds align per death section; rendered as the second group.
const Y_ALIGN_OPTIONS: AlignOption[] = [
  { label: 'Top', kind: 'top', Icon: AlignVerticalTopIcon },
  { label: 'Middle', kind: 'centerY', Icon: AlignVerticalCenterIcon },
  { label: 'Bottom', kind: 'bottom', Icon: AlignVerticalBottomIcon },
];

interface DistributeOption {
  label: string;
  axis: DistributeAxis;
  Icon: ComponentType<IconProps>;
}

const DISTRIBUTE_OPTIONS: DistributeOption[] = [
  {
    label: 'Distribute vertical spacing',
    axis: 'vertical',
    Icon: DistributeVerticalSpacingIcon,
  },
  {
    label: 'Distribute horizontal spacing',
    axis: 'horizontal',
    Icon: DistributeHorizontalSpacingIcon,
  },
];

const ICON_SIZE = 16;

export interface AlignmentPickerProps {
  onAlign: (kind: AlignKind) => void;
  onDistribute: (axis: DistributeAxis) => void;
  canDistribute: boolean;
}

/**
 * Toolbar dropdown with bulk align/distribute actions for selected diagram
 * nodes. Callers render it only when two or more alignable nodes are selected.
 * The menu groups align-X, align-Y, and distribute actions with dividers,
 * matching the Figma design.
 */
export function AlignmentPicker({
  onAlign,
  onDistribute,
  canDistribute,
}: AlignmentPickerProps) {
  return (
    <ButtonWithPopover
      variant="transparent"
      size="xs"
      label={<AlignHorizontalLeftIcon size={ICON_SIZE} />}
      aria-label="Align"
      highlightWhenOpen
      popperProps={{
        // Anchor the menu to the bottom-left of the trigger so it opens
        // downward and extends to the right, matching the other pickers.
        anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
        transformOrigin: { horizontal: 'left', vertical: 'top' },
        PaperProps: { sx: { minWidth: 226 } },
      }}
    >
      {X_ALIGN_OPTIONS.map(({ label, kind, Icon }) => (
        <MenuItem
          key={kind}
          icon={<Icon size={ICON_SIZE} />}
          label={label}
          onClick={() => onAlign(kind)}
        />
      ))}
      <Divider noMargin sx={{ borderColor: COLORS.GRAY[200] }} />
      {Y_ALIGN_OPTIONS.map(({ label, kind, Icon }) => (
        <MenuItem
          key={kind}
          icon={<Icon size={ICON_SIZE} />}
          label={label}
          onClick={() => onAlign(kind)}
        />
      ))}
      <Divider noMargin sx={{ borderColor: COLORS.GRAY[200] }} />
      {DISTRIBUTE_OPTIONS.map(({ label, axis, Icon }) => (
        <MenuItem
          key={axis}
          icon={<Icon size={ICON_SIZE} />}
          label={label}
          disabled={!canDistribute}
          onClick={() => {
            // MUI disables clicks via pointer-events only; guard explicitly.
            if (canDistribute) onDistribute(axis);
          }}
        />
      ))}
    </ButtonWithPopover>
  );
}
